/**
 * Parse prompt text back into individual fields
 * Handles the format created by promptBuilderController.buildPrompt
 * 
 * Note: This function handles the STRUCTURED format (legacy format).
 * For format detection, use detectPromptFormat from promptFormatDetector.js
 */

/**
 * Parse promptText into individual fields (STRUCTURED format)
 * @param {string} promptText - The combined prompt text
 * @returns {Object} Object with persona, format, context, task, dos, donts, examples
 */
export const parsePromptText = (promptText) => {
  if (!promptText || typeof promptText !== 'string') {
    return {
      persona: '',
      format: '',
      context: '',
      task: '',
      dos: '',
      donts: '',
      examples: '',
    };
  }

  // Split by double newlines (the separator used in buildPrompt)
  const parts = promptText.split('\n\n').map(part => part.trim()).filter(part => part.length > 0);

  const result = {
    persona: '',
    format: '',
    context: '',
    task: '',
    dos: '',
    donts: '',
    examples: '',
  };

  // Track which field we're currently parsing
  let currentField = null;

  for (const part of parts) {
    // Check for Context:
    if (part.startsWith('Context: ')) {
      currentField = 'context';
      result.context = part.substring('Context: '.length).trim();
    }
    // Check for Task:
    else if (part.startsWith('Task: ')) {
      currentField = 'task';
      result.task = part.substring('Task: '.length).trim();
    }
    // Check for Requirements: (dos)
    else if (part.startsWith('Requirements: ')) {
      currentField = 'dos';
      result.dos = part.substring('Requirements: '.length).trim();
    }
    // Check for Constraints: (donts)
    else if (part.startsWith('Constraints: ')) {
      currentField = 'donts';
      result.donts = part.substring('Constraints: '.length).trim();
    }
    // Check for Format your response as:
    else if (part.startsWith('Format your response as: ')) {
      currentField = 'format';
      result.format = part.substring('Format your response as: '.length).trim();
    }
    // Check for Examples/Reference:
    else if (part.startsWith('Examples/Reference: ')) {
      currentField = 'examples';
      result.examples = part.substring('Examples/Reference: '.length).trim();
    }
    // If we're currently parsing a field, append to it
    else if (currentField) {
      result[currentField] += '\n\n' + part;
    }
    // If it doesn't match any prefix and no current field, it's likely the persona (first part, no prefix)
    else if (!result.persona) {
      currentField = 'persona';
      result.persona = part;
    }
    // If persona is already set and no current field, append to persona (for backward compatibility)
    else {
      currentField = 'persona';
      result.persona += '\n\n' + part;
    }
  }

  return result;
};

// Matches a paragraph that opens with a short "Label: " prefix, e.g.
// "Context: ...", "Objective: ...", "Response Structure: ...". Used to
// discover sections generically, since not every structured prompt uses the
// fixed Persona/Context/Task/Requirements/Constraints/Format/Examples set
// that parsePromptText() above expects (buildPrompt's legacy format).
// No digits in the label class on purpose — it keeps mid-content lines like
// "Step 2 - Ask:" from being mistaken for a new top-level section.
const SECTION_LABEL_PATTERN = /^([A-Z][A-Za-z /'’()-]{1,50}):\s*([\s\S]*)$/;

/**
 * Parse promptText into an ordered list of labelled sections, discovering
 * labels dynamically instead of matching a fixed field set. Any leading
 * unlabelled paragraph (e.g. a persona line with no "Label:" prefix) is
 * kept as a "Persona" section for display parity with the legacy parser.
 * @param {string} promptText - The combined prompt text
 * @returns {Array<{key: string, title: string, content: string}>}
 */
export const parseGenericSections = (promptText) => {
  if (!promptText || typeof promptText !== 'string') return [];

  const parts = promptText.split('\n\n').map((part) => part.trim()).filter((part) => part.length > 0);
  const sections = [];

  parts.forEach((part) => {
    const match = part.match(SECTION_LABEL_PATTERN);
    if (match) {
      sections.push({ key: `section-${sections.length}`, title: match[1].trim(), content: match[2].trim() });
    } else if (sections.length === 0) {
      sections.push({ key: 'persona', title: 'Persona', content: part });
    } else {
      const last = sections[sections.length - 1];
      last.content = last.content ? `${last.content}\n\n${part}` : part;
    }
  });

  return sections;
};

